import { Navigate, Route, Routes } from "react-router-dom";
import Login from "../pages/auth/login";
import CreateTenant from "../pages/auth/create-tenant";
import CRMConnections from "../pages/integration/integration";
import FacebookLogin from "../pages/integration/facebook";
import EcommerceIntegration from "../pages/integration/e-commerce";
import NextBestAction from "../pages/optimization/next-best-action";
import GeoLift from "../pages/measurement/geo-lift";
import BidOptimizer from "../pages/optimization/bid-optimizer";
import BidOptimizerDashboard from "../pages/optimization/bid-optimizer/id";
import MarketingMixModel from "../pages/measurement/marketing-mix-model";
import MMMDashboard from "../pages/measurement/marketing-mix-model/id";
import JobCreate from "../pages/job-create";
import DataSourceCreate from "../pages/data-source-create";
import Users from "../pages/users";
import CreateUser from "../pages/users/create-user";
import UpdateUser from "../pages/users/update-user"; 
import SyntheticControlLanding from "../pages/measurement/geo-lift/components/SyntheticControlLanding";
import mixpanel from "mixpanel-browser";
import { jwtDecode } from "jwt-decode";
import PrivateRoute from "./PrivateRoute";
import NotFound from "../pages/NotFound";
import Dashboard from "../pages/dashboard";
import GeoLiftStudy from "../pages/measurement/geo-lift/components/GeoLiftStudy";
import MMMLanding from "../pages/measurement/marketing-mix-model/components/MMMLanding";
import MainDashboard from "../components/MainDashboard";
import BidLanding from "../pages/optimization/bid-optimizer/BidLanding";
import ChatInterface from "../pages/chatBot/ChatInterface";
import Index from "../pages/MarketingLanding";
import PowerCalculatorLanding from "../pages/power-calculator/components/PowerCalculatorLanding";
import PowerCalculatorList from "../pages/power-calculator";
import PowerCalculatorStudy from "../pages/power-calculator/components/PowerCalculatorStudy";
import InsightsLanding from "../pages/chatBot/InsightsLanding";
// New AI-native pages (Sprint B2 / F5)
import HistoryPage from "../pages/history";
import SettingsPage from "../pages/settings-page";
import WorkspacePage from "../pages/agent-studio/WorkspacePage";
import AgentBuilderPage from "../pages/agent-studio/AgentBuilderPage";
import SharedCanvasPage from "../pages/agent-studio/SharedCanvasPage";
import AssistantRedirect from "../pages/agent-studio/AssistantRedirect";
import HomePage from "../pages/home";
import ExperimentsPage from "../pages/experiments";
import MMMPageNew from "../pages/mmm";
import MMMJobsCreate from "../pages/measurement/marketing-mix-model/components/MMMJobsCreate";
import BidOptimizerPage from "../pages/bid-optimizer";
import BidOptimizerCreate from "../pages/bid-optimizer/create";
import BidOptimizerDetail from "../pages/bid-optimizer/[id]";
import { WithMode } from "../pages/command-center/components";
import CommandCenterTests from "../pages/command-center/CommandCenterTests";
import CommandCenterCalendar from "../pages/command-center/CommandCenterCalendar";
import CommandCenterLibrary from "../pages/command-center/CommandCenterLibrary";
import CommandCenterRules from "../pages/command-center/CommandCenterRules";
import MlTestFlow from "../pages/command-center/MlTestFlow";
import DeckTestFlow from "../pages/command-center/DeckTestFlow";

mixpanel.init("590857ca7f2572c75c7f1c443e749dfc", {
  debug: true,
  track_pageview: true,
  persistence: "localStorage",
});

if (localStorage.getItem("authToken") != null) {
  const decoded = jwtDecode(localStorage.getItem("authToken") as string);
  mixpanel.identify(decoded.sub);
  mixpanel.people.set(decoded);
}

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/auth/login" element={<Login />} />
      <Route path="/landing" element={<Index />} />
      {/* Public read-only shared canvas (Agent Studio) */}
      <Route path="/share/:token" element={<SharedCanvasPage />} />
      <Route element={<PrivateRoute />}>
        <Route path="/agent" element={<InsightsLanding />} />
        <Route path="/agent/:chatid" element={<ChatInterface />} />

        {/* New Home (F2) — intelligence feed replaces old chart dashboard */}
        <Route path="/" element={<Dashboard />} />
        {/* Old dashboard still accessible for now */}
        <Route path="/dashboard/dashboard" element={<Dashboard />} />

        {/* Power Calculator Routes */}
        <Route
          path="/power-calculator/landing"
          element={<PowerCalculatorLanding />}
        />
        <Route
          path="/power-calculator/list"
          element={<PowerCalculatorList />}
        />
        <Route
          path="/power-calculator/:id"
          element={<PowerCalculatorStudy />}
        />
        {/* <Route path="/dashboard/dashboard" element={<Dashboard />} /> */}
        <Route path="/auth/create-tenant" element={<CreateTenant />} />
        <Route path="/crm/integrations" element={<CRMConnections />} />
        <Route path="/facebook/integrations" element={<FacebookLogin />} />
        <Route
          path="/e-commerce/integrations"
          element={<EcommerceIntegration />}
        />
        <Route
          path="/integrations/google-callback"
          element={<MainDashboard />}
        />
        <Route
          path="/optimization/next-best-action"
          element={<NextBestAction />}
        />

        <Route
          path="/measurement/geo-lift"
          Component={SyntheticControlLanding}
        />
        <Route
          path="/measurement/synthetic_control_list"
          element={<GeoLift />}
        />
        {/* <Route path="/measurement/geo-lift/:id" Component={GeoLiftDashboard} /> */}

        <Route path="/optimization/landing" element={<BidLanding />} />
        <Route path="/optimization/bid-optimizer" element={<BidOptimizer />} />
        <Route
          path="/optimization/bid-optimizer/:id"
          Component={BidOptimizerDashboard}
        />
        <Route
          path="/planning/marketing-mix-model"
          element={<MarketingMixModel />}
        />
        <Route
          path="/planning/marketing-mix-model-landing"
          element={<MMMLanding />}
        />

        {/* <Route path="/auth/hubspot/callback" Component={HubSpotOAuthCallback} /> */}
        <Route
          path="/planning/marketing-mix-model/:id"
          Component={MMMDashboard}
        />
        
        <Route path="/jobs/:jobType" element={<JobCreate />} />
        <Route
          path="/integrations/create/:dataSourceTypes"
          element={<DataSourceCreate />}
        />

        <Route path="/users" element={<Users />} />
        <Route path="/users/create-user" element={<CreateUser />} />
        <Route path="/users/update-user/:userId" element={<UpdateUser />} />

        {/* ── New AI-native routes (Sprint B2) ─────────────────────────── */}
        {/* Ask Adrsta — Agent Studio Workspace (chat + canvas). Covers the original
            AI Assistant (now the "Study Assistant" agent) plus built-in and custom agents.
            /assistant redirects here; the old page stays in pages/assistant. */}
        <Route path="/assistant" element={<AssistantRedirect />} />
        {/* One route (optional segments) so the page stays mounted while a new chat gets its URL:
            /workspace · /workspace/:conversationId · /workspace/assistant/:sessionId */}
        <Route path="/workspace/:first?/:second?" element={<WorkspacePage />} />
        <Route path="/agent-builder" element={<AgentBuilderPage />} />

        {/* Experiments — new page (F4) */}
        <Route path="/experiments" element={<ExperimentsPage />} />
        {/* One route covers create (fresh uuid in the path), view, and edit
            (?edit=true) — there's no separate "/new" path anymore since the
            uuid is always minted client-side and put directly in the URL,
            matching the create/view/edit pattern used elsewhere in the app. */}
        <Route path="/experiments/geo-lift/:id" Component={GeoLiftStudy} />

        {/* MMM — landing (list + AI Mode / New MMM).
            Unlike Experiments, create AND view both go through the same
            MMMJobsCreate wizard — matching the pre-existing /jobs/MMM
            behavior (no separate edit/view dashboard, no edit button).
            Create: fresh uuid in the path, no state. View: existing id +
            location.state {jobConfigId, jobName, step, viewOnly: true}. */}
        <Route path="/mmm" element={<MMMPageNew />} />
        <Route path="/mmm/:id" Component={MMMJobsCreate} />

        {/* Bid Optimizer — landing (list + AI Mode / New MMM),
            themed creation wizard, and detail/results view — mirrors the
            Experiments/MMM pattern. Legacy /optimization/bid-optimizer*
            routes above are untouched and still serve the old antd flow. */}
        <Route path="/bid-optimizer" element={<BidOptimizerPage />} />
        <Route path="/bid-optimizer/create/:id" element={<BidOptimizerCreate />} />
        <Route path="/bid-optimizer/:id" element={<BidOptimizerDetail />} />

        {/* Command Center (demo) — two menus: "ml" (Command Center + ML,
            Adrsta runs power analysis + readout) and "deck" (no ML, partner
            design/results decks are uploaded). Static demo numbers; tests are
            saved via /api/v1/command-center. */}
        <Route path="/command-center" element={<Navigate to="/command-center/ml" replace />} />
        <Route path="/command-center/ml/new" element={<MlTestFlow />} />
        <Route path="/command-center/ml/tests/:id" element={<MlTestFlow />} />
        <Route path="/command-center/deck/new" element={<DeckTestFlow />} />
        <Route path="/command-center/deck/tests/:id" element={<DeckTestFlow />} />
        <Route path="/command-center/:mode" element={<WithMode>{m => <CommandCenterTests key={m} mode={m} />}</WithMode>} />
        <Route path="/command-center/:mode/calendar" element={<WithMode>{m => <CommandCenterCalendar key={m} mode={m} />}</WithMode>} />
        <Route path="/command-center/:mode/library" element={<WithMode>{m => <CommandCenterLibrary key={m} mode={m} />}</WithMode>} />
        <Route path="/command-center/:mode/rules" element={<WithMode>{m => <CommandCenterRules key={m} mode={m} />}</WithMode>} />

        {/* Campaigns — new route (F7), falls back to InsightsLanding */}
        <Route path="/campaigns" element={<InsightsLanding />} />

        {/* History — new page ────────────────────────────────────────── */}
        <Route path="/history" element={<HistoryPage />} />

        {/* Reports — placeholder ──────────────────────────────────────── */}
        <Route path="/reports" element={<HistoryPage />} />

        {/* Settings — new page ────────────────────────────────────────── */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/:tab" element={<SettingsPage />} />

        {/* Power Calculator — new clean route (F4b) */}
        <Route path="/power-calculator" element={<PowerCalculatorLanding />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
